import type { WidgetWidth } from "@playbookai/types";
import { WIDGET_LABELS } from "@playbookai/widget-schemas";
import { reorderIds } from "../../molecules/SortableList/SortableList.js";
import type { DashboardWidgetCategory } from "../../widgets/types.js";
import type { DashboardItem } from "./types.js";

export const WIDTH_LABEL: Record<WidgetWidth, string> = {
  small: "Small",
  medium: "Medium",
  wide: "Wide",
  full: "Full",
};

/* The spans follow the dashboard's OWN width (its named container), not the
   window (PB-5436): 63rem and 39rem are Home's dashboard width at the `lg`
   and `sm` windows (1024px, 640px) minus the 1rem scrollbar allowance. Hosts
   with more padding switch a little later; no host ever shows more columns
   than before. The half-width step moved too once the branch probe showed a
   VSM monitor at 1024px with the rail open still pairing cards in ~500px. */
export const COL_SPAN_CLASS: Record<3 | 4 | 6 | 12, string> = {
  3: "col-span-12 @min-[39rem]/dashboard:col-span-6 @min-[63rem]/dashboard:col-span-3",
  4: "col-span-12 @min-[39rem]/dashboard:col-span-6 @min-[63rem]/dashboard:col-span-4",
  6: "col-span-12 @min-[63rem]/dashboard:col-span-6",
  12: "col-span-12",
};

/** Add-menu group order (AI last, mirroring the oracle). */
export const CATEGORY_ORDER: readonly DashboardWidgetCategory[] = [
  "Strategy",
  "Operations",
  "Personal",
  "AI",
];

export function widgetLabel(item: DashboardItem): string {
  return item.title ?? WIDGET_LABELS[item.kind] ?? item.kind;
}

export function shallowEqualArray(
  a: readonly unknown[],
  b: readonly unknown[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

/** Move `activeId` to `overId`'s slot, reusing the molecule's pure helper.
 *  Returns the same array reference on a no-op so callers can skip `onReorder`. */
export function reorderItems(
  items: readonly DashboardItem[],
  activeId: string,
  overId: string,
): readonly DashboardItem[] {
  const ids = items.map((i) => i.instanceId);
  const nextIds = reorderIds(ids, activeId, overId);
  if (nextIds === ids) return items;
  const byId = new Map(items.map((i) => [i.instanceId, i] as const));
  return nextIds
    .map((id) => byId.get(id))
    .filter((i): i is DashboardItem => i !== undefined);
}
